import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SIGNUP_SOURCE_AUTO } from '@/constants/services';
import {
  EVENT_MODAL_STATE_SIGNUP_CLOSE,
  EVENT_MODAL_STATE_SIGNUP_OPEN,
} from '@/hooks/useAuthModalState';
import { selectAuth, signupUser } from '@/redux/modules/auth';
import { ISignupForm } from '@/services/types/auth/ISignupForm';
import { getReCaptchaToken } from '@/utility/recaptcha';
import {
  EVENT_NAME_CLOSE_SIGN_UP,
  EVENT_NAME_SIGN_UP,
  registerAuthenticateEvent,
} from '@/utility/session';
import { triggerLoginModal } from '@/utility/triggerLoginModal';

import SignUpModal from './SignUpModal';

type TSignUpModalContainerProps = {
  isAutoPage?: boolean;
};

const SignUpModalContainer = ({ isAutoPage }: TSignUpModalContainerProps) => {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const [isOpen, setIsOpen] = useState(false);
  const [isClosable, setIsClosable] = useState(true);

  const onSignup = useCallback(
    (data: ISignupForm) =>
      dispatch(signupUser({ ...data, signupSource: isAutoPage ? SIGNUP_SOURCE_AUTO : undefined })),
    [dispatch, isAutoPage],
  );

  const onClose = useCallback<() => void>(() => {
    setIsOpen(false);
    // Dispatch modal state event for popover tracking
    document.dispatchEvent(new CustomEvent(EVENT_MODAL_STATE_SIGNUP_CLOSE));
  }, []);

  // Track modal open state changes
  useEffect(() => {
    if (isOpen) {
      document.dispatchEvent(new CustomEvent(EVENT_MODAL_STATE_SIGNUP_OPEN));
    } else {
      document.dispatchEvent(new CustomEvent(EVENT_MODAL_STATE_SIGNUP_CLOSE));
    }
  }, [isOpen]);

  useEffect(() => {
    registerAuthenticateEvent(EVENT_NAME_CLOSE_SIGN_UP, onClose); // TODO: Do this via redux post R1
    registerAuthenticateEvent(EVENT_NAME_SIGN_UP, (event?: CustomEvent) => {
      if (!auth.isAuthenticated) setIsOpen(true);
      setIsClosable(event?.detail?.closable ?? true);
    });
  }, [auth.isAuthenticated, onClose]);

  const onLogin = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    triggerLoginModal(0, isClosable);
  };

  return (
    <SignUpModal
      signupFormProps={{
        onSignup,
        error: auth.error,
        reCaptchaToken: getReCaptchaToken(),
      }}
      isAuthenticated={auth.isAuthenticated}
      isOpen={isOpen}
      isClosable={isClosable}
      onLogin={onLogin}
      onClose={onClose}
    />
  );
};

export default SignUpModalContainer;
